import type { ContextWarningPayload } from "../types/index.js";

interface ContextWarningProps {
	warning: ContextWarningPayload;
	onDismiss: () => void;
}

/** Map reason codes to human-readable messages. */
function getWarningMessage(reason: ContextWarningPayload["reason"]): string {
	switch (reason) {
		case "project_not_found":
			return "The selected project could not be found. Responding without project context.";
		case "project_context_unavailable":
			return "Project data is temporarily unavailable. Responding without project context.";
		case "project_not_indexed":
			return "This project has no indexed documents yet. Responses may lack document context.";
		case "embedding_generation_failed":
			return "Document search is temporarily unavailable. Responding without document context.";
		case "document_search_failed":
			return "Document retrieval failed. Responding without document context.";
	}
	// Exhaustiveness check — TypeScript will error here if a new reason is added
	// to ContextWarningPayload["reason"] without updating this switch.
	const _exhaustiveCheck: never = reason;
	return _exhaustiveCheck;
}

/**
 * Inline warning banner displayed above the chat when project context
 * could not be loaded. Yellow/amber styling with a dismiss button.
 * Auto-clears on next message send (handled by useChat).
 */
export default function ContextWarning({ warning, onDismiss }: ContextWarningProps) {
	return (
		<div className="context-warning" role="alert">
			<span className="context-warning-text">
				{getWarningMessage(warning.reason)}
			</span>
			<button
				className="context-warning-dismiss"
				onClick={onDismiss}
				type="button"
				aria-label="Dismiss warning"
			>
				&times;
			</button>
		</div>
	);
}
